import {
    Button,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Tooltip,
    Typography
} from "@material-ui/core"
import React, { FunctionComponent, useEffect, useMemo, useState } from "react"
import { updateFirstTranslate, updateSourceLanguage, updateTargetLanguage } from "./reducer"
import { useBatchPageContext } from "./useBatchPageContext"
import styles from "./BatchTranslate.module.scss"
import { Step, useStepControlsContext } from "../../features/StepControls/useStepControlsContext"
import { StepControls } from "../../features/StepControls/StepControls"
import { WarningOutlined } from "@material-ui/icons"
import { LoadingButton } from "../../components/LoadingButton"

export const BatchTranslate: FunctionComponent = props => {
    const { dispatch, state, translateText } = useBatchPageContext()
    const { show, activate, deactivate } = useStepControlsContext()

    const [loadingTranslate, setLoadingTranslate] = useState(false)

    const kickoffTranslate = async () => {
        setLoadingTranslate(true)
        await translateText().then(() => {
            setLoadingTranslate(false)
            if (state.firstTranslate) {
                dispatch(updateFirstTranslate(false))
            }
        })
    }

    const canProceed = useMemo(() => {
        return (
            !state.containsOperationErrors && !state.containsValidationErrors && !loadingTranslate
        )
    }, [state.containsOperationErrors, state.containsValidationErrors, loadingTranslate])

    const displayTextWarning = useMemo(() => {
        return (
            state.batchItems.filter(item => item.target && item.include).length > 0 &&
            state.firstTranslate
        )
    }, [state.batchItems, state.firstTranslate])

    useEffect(() => {
        if (canProceed) {
            activate(Step.NEXT)
            activate(Step.BACK)
        } else {
            deactivate(Step.NEXT)
            deactivate(Step.BACK)
        }
    }, [canProceed])

    useEffect(() => {
        show(Step.NEXT)
        activate(Step.NEXT)
        show(Step.BACK)
        activate(Step.BACK)
    }, [])

    const disableTranslateAll = state.containsValidationErrors || loadingTranslate

    const targetTextWarningMessage =
        'Warning: One or more rows contain target text, by clicking "Translate All" the target contents of these rows will be overwritten.'

    return (
        <Grid container spacing={3} direction={"column"}>
            <Grid item>
                <Typography variant="caption">
                    Choose a source/target languages for translation.
                </Typography>
            </Grid>

            <Grid item>
                <Grid item className={styles.TranslateOptions}>
                    <Typography variant="body1">Translate Text Options</Typography>
                </Grid>
                <Grid
                    container
                    direction={"row"}
                    id={"select-languages-for-batch-translate"}
                    spacing={2}
                >
                    <Grid item>
                        <FormControl variant="outlined" id="select-source-language-control">
                            <InputLabel id="select-source-language-label">
                                Source Language
                            </InputLabel>
                            <Select
                                labelId="select-source-language-label"
                                id="select-source-language"
                                onChange={(e: any) => {
                                    dispatch(updateSourceLanguage(e.target.value))
                                }}
                                label="Source Language"
                                value={state.sourceLanguage ? state.sourceLanguage : ""}
                            >
                                {state.languageOptions.source.map(language => (
                                    <MenuItem key={language.name} value={language.id}>
                                        {language.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item>
                        <FormControl variant="outlined" id="select-target-language-control">
                            <InputLabel id="select-target-language-label">
                                Target Language
                            </InputLabel>
                            <Select
                                labelId="select-target-language-label"
                                id="select-target-language"
                                onChange={(e: any) => {
                                    dispatch(updateTargetLanguage(e.target.value))
                                }}
                                label="Target Language"
                                value={state.targetLanguage ? state.targetLanguage : ""}
                            >
                                {state.languageOptions.target.map(language => (
                                    <MenuItem key={language.name} value={language.id}>
                                        {language.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item>
                <Grid container direction="row" spacing={0}>
                    <Grid item>
                        <LoadingButton
                            text={"Translate All"}
                            disabled={disableTranslateAll}
                            onClick={kickoffTranslate}
                            loading={loadingTranslate}
                            buttonId={"translate-text-btn"}
                        />
                    </Grid>
                    <Grid item className={styles.warningContainer}>
                        {!loadingTranslate && displayTextWarning && (
                            <Tooltip title={targetTextWarningMessage} placement={"right"}>
                                <WarningOutlined
                                    id="translateWarningIcon"
                                    data-testid="translateWarningIcon"
                                    fontSize="large"
                                    htmlColor="gold"
                                />
                            </Tooltip>
                        )}
                    </Grid>
                </Grid>
            </Grid>
            <Grid item>
                <StepControls />
            </Grid>
        </Grid>
    )
}
